import {
  Box,
  Button,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { djs } from '@duke-hq/djs';
import { getUserFullName } from '@duke-hq/lib';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { CopyToClipboard } from '@duke-hq/ui/components/CopyToClipboard/CopyToClipboard';
import { CopyToClipboardPopover } from '@duke-hq/ui/components/CopyToClipboardPopover/CopyToClipboardPopover';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import {
  EmployerPlacementStatusBadge,
  JourneyStatusBadge,
} from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { plural, t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { StudentWorkPlacementJourneyStatus } from '@duke-hq/svc-api-db/client';
import {
  canStartPlacement,
  PRE_PLACEMENT_CHECKLIST_JOURNEY_STATUSES,
} from '@duke-hq/svc-api/lib/workPlacement/journeyStatus';
import { isActiveEmployerPlacement } from '@duke-hq/svc-api/lib/workPlacement/employerPlacementStatus';
import {
  ArrowUpDown,
  BadgeCheck,
  Ellipsis,
  FileText,
  PauseCircle,
  TriangleAlert,
  User,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { CreatePlacementModal } from '../CreatePlacementModal';
import { PlacementActions } from './PlacementActions';
import type { ChangeableStage } from './journeyStages';
import { ContractSection } from './ContractSection';
import { placingBlockedReason } from './contractParty';
import { ChangeStageModal, canChangeStage } from './ChangeStageModal';
import { ReverseMarketingSection } from './ReverseMarketingSection';
import { PlacementDetailsSection } from './PlacementDetailsSection';
import { PlacementHistorySection } from './PlacementHistorySection';
import { PlacementStepper } from './PlacementStepper';
import { RequiredChecksSection } from './RequiredChecksSection';
import { testIds } from './testIds';
import { LoginAsButton } from '../../components/LoginAsButton';

type Props = {
  enrolmentId: number;
  onClose: () => void;
};

function ReviewAlert({
  pendingDocuments,
  pendingNominations,
}: {
  pendingDocuments: number;
  pendingNominations: number;
}): React.ReactElement | null {
  if (pendingDocuments === 0 && pendingNominations === 0) {
    return null;
  }

  const documentCount = plural(pendingDocuments, { one: '# document', other: '# documents' });
  const nominationCount = plural(pendingNominations, {
    one: '# nomination',
    other: '# nominations',
  });

  return (
    <HStack
      data-testid={testIds.reviewAlert}
      bg="bg.warning-subtle"
      borderWidth="1px"
      borderColor="warning"
      borderRadius="xl"
      px={4}
      py={3}
      gap={3}
      align="center"
    >
      <Icon as={TriangleAlert} boxSize={iconSize.md} color="warning.emphasis" flexShrink={0} />
      <Text fontSize="sm">
        {pendingDocuments > 0 && pendingNominations > 0 ? (
          <Trans>
            {documentCount} and {nominationCount} need review
          </Trans>
        ) : pendingDocuments > 0 ? (
          <Plural
            value={pendingDocuments}
            one="# document needs review"
            other="# documents need review"
          />
        ) : (
          <Plural
            value={pendingNominations}
            one="# nomination needs review"
            other="# nominations need review"
          />
        )}
      </Text>
    </HStack>
  );
}

export function StudentDrawer({ enrolmentId, onClose }: Props): React.ReactElement {
  const navigate = useNavigate();
  const changeStageModal = useDisclosure();
  const [preselectedStage, setPreselectedStage] = useState<ChangeableStage | null>(null);
  const [placing, setPlacing] = useState<Pick<
    React.ComponentProps<typeof CreatePlacementModal>,
    'courseEnrolmentId' | 'student' | 'contract'
  > | null>(null);
  const toast = useToast();

  const [selectedEnrolmentId, setSelectedEnrolmentId] = useState<number | null>(null);

  const { data: studentEnrolments } =
    trpcAdmin.workPlacements.placements.listStudentEnrolments.useQuery({ enrolmentId });

  // Tabs default to the enrolment the drawer was opened from until the admin
  // selects another — never auto-switch to a different enrolment once the tab
  // list loads.
  const enrolmentTabs = studentEnrolments ?? [];
  const activeEnrolmentId = selectedEnrolmentId ?? enrolmentId;

  const {
    data,
    isLoading,
    isPlaceholderData: studentIsPlaceholder,
    refetch: refetchStudent,
  } = trpcAdmin.workPlacements.placements.getStudent.useQuery(
    { enrolmentId: activeEnrolmentId },
    { placeholderData: keepPreviousData },
  );

  const {
    data: journeyData,
    isPlaceholderData: journeyIsPlaceholder,
    refetch: refetchJourney,
  } = trpcAdmin.workPlacements.placements.journeyByEnrolment.useQuery(
    { courseEnrolmentId: activeEnrolmentId },
    { placeholderData: keepPreviousData },
  );

  const refetchAll = () => {
    void refetchStudent();
    void refetchJourney();
  };

  const setOnHoldMutation = trpcAdmin.workPlacements.placements.setOnHold.useMutation({
    onSuccess: () => {
      refetchAll();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to update on hold`, description: error.message });
    },
  });

  const journey = journeyData?.journey ?? null;
  // While a tab switch is still showing the previous enrolment's data, keep the
  // pre-placement actions disabled so they can't act on a stale journey status.
  const journeyPlaced = journeyIsPlaceholder || journey?.status === 'PLACED';
  const placements = useMemo(() => journeyData?.placements ?? [], [journeyData]);

  const journeyId = journey?.id ?? null;
  const journeyOnHold = journey?.onHold === true;

  const activePlacement = useMemo(
    () => placements.find((placement) => isActiveEmployerPlacement(placement.status)),
    [placements],
  );

  const historyPlacements = useMemo(
    () =>
      [...placements]
        .filter((placement) => placement.id !== activePlacement?.id)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime() || b.id.localeCompare(a.id)),
    [placements, activePlacement],
  );

  const journeyPlaceable = journey != null && canStartPlacement(journey.status);

  const { data: unitsToPlace, isError: unitsLoadFailed } =
    trpcAdmin.workPlacements.placements.availableUnits.useQuery(
      { courseEnrolmentId: activeEnrolmentId },
      { enabled: journeyPlaceable },
    );

  const enrolmentWithdrawn = journeyData?.enrolmentWithdrawn === true;

  const { data: checklist } = trpcAdmin.workPlacements.placements.getChecklist.useQuery(
    { journeyId: journeyId ?? '' },
    { enabled: journeyId != null && !enrolmentWithdrawn },
  );
  const { data: nominations } = trpcAdmin.workPlacements.placements.listNominations.useQuery(
    { journeyId: journeyId ?? '' },
    { enabled: journeyId != null && !enrolmentWithdrawn },
  );

  const canReview =
    journey != null &&
    !journeyIsPlaceholder &&
    journey.status !== StudentWorkPlacementJourneyStatus.COMPLETED;
  const canUploadDocuments =
    journey != null &&
    !journeyIsPlaceholder &&
    !journeyOnHold &&
    PRE_PLACEMENT_CHECKLIST_JOURNEY_STATUSES.includes(journey.status);
  const reviewsActionable = canReview && !journeyOnHold && !enrolmentWithdrawn;
  const pendingDocuments = reviewsActionable
    ? (checklist?.items ?? []).filter((item) => item.status === 'PENDING_REVIEW').length
    : 0;
  const pendingNominations = reviewsActionable
    ? (nominations ?? []).filter(
        (nomination) =>
          nomination.reviewStatus === 'PENDING_REVIEW' || nomination.editedSinceReviewAt != null,
      ).length
    : 0;

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (data == null) {
    return (
      <Center h="100vh">
        <Text>
          <Trans>Student not found</Trans>
        </Text>
      </Center>
    );
  }

  const { student, course, enrolment } = data;

  const contract = journeyData?.contract ?? null;
  const placingCanBeChecked = journeyPlaceable && !studentIsPlaceholder && !journeyIsPlaceholder;
  const placeBlockedReason = placingCanBeChecked
    ? placingBlockedReason({
        enrolmentWithdrawn,
        journeyOnHold,
        contract,
        unitsToPlace: unitsToPlace?.length,
        unitsLoadFailed,
      })
    : null;
  const canPlaceStudent =
    placingCanBeChecked && course != null && unitsToPlace != null && placeBlockedReason == null;

  const changeStageAvailable =
    !journeyIsPlaceholder &&
    canChangeStage({
      journeyStatus: journey?.status ?? null,
      onHold: journey?.onHold ?? false,
    });

  const placementActions =
    journey != null ? (
      <PlacementActions
        activePlacement={activePlacement ?? null}
        canPlaceStudent={canPlaceStudent}
        placeBlockedReason={placeBlockedReason}
        courseEnrolmentId={activeEnrolmentId}
        onPlace={() => {
          if (course != null && contract != null) {
            setPlacing({
              courseEnrolmentId: activeEnrolmentId,
              student: {
                fullName: fullName ?? '',
                courseLabel: course.title ?? '',
                usi: student.avetmissIdentifier ?? null,
              },
              contract,
            });
          }
        }}
        onChanged={refetchAll}
      />
    ) : null;

  const fullName = getUserFullName({
    first_name: student.firstName,
    last_name: student.lastName,
  });

  return (
    <>
      <DrawerHeader bg="bg.subtle" borderBottomWidth="1px" borderBottomColor="border.default" p={5}>
        <Flex justify="space-between" align="start" gap={4}>
          {journey != null ? (
            <HStack gap={2} align="center" flexWrap="wrap" data-testid={testIds.onHoldBadge}>
              {journey.status === StudentWorkPlacementJourneyStatus.PLACED &&
              activePlacement != null ? (
                <EmployerPlacementStatusBadge
                  status={activePlacement.status}
                  onHold={journey.onHold}
                />
              ) : (
                <JourneyStatusBadge status={journey.status} onHold={journey.onHold} />
              )}
            </HStack>
          ) : (
            <Box />
          )}
          <HStack gap={1} flexShrink={0}>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label={t`more actions`}
                icon={<Icon as={Ellipsis} />}
                variant="ghost"
                size="sm"
              />
              <MenuList>
                <MenuItem
                  icon={<PauseCircle size={iconSize.md} />}
                  isDisabled={journeyId == null || setOnHoldMutation.isPending}
                  onClick={() => {
                    if (journeyId != null && journey != null) {
                      setOnHoldMutation.mutate({
                        journeyId,
                        onHold: !journey.onHold,
                      });
                    }
                  }}
                  data-testid={testIds.onHoldMenuItem}
                >
                  {journeyOnHold ? <Trans>Take off hold</Trans> : <Trans>Mark as on hold</Trans>}
                </MenuItem>
                {changeStageAvailable ? (
                  <MenuItem
                    icon={<ArrowUpDown size={iconSize.md} />}
                    onClick={() => {
                      setPreselectedStage(null);
                      changeStageModal.onOpen();
                    }}
                    data-testid={testIds.changeStageMenuItem}
                  >
                    <Trans>Change stage</Trans>
                  </MenuItem>
                ) : null}
                <MenuItem
                  icon={<FileText size={iconSize.md} />}
                  onClick={() => navigate(`/enrolment/${activeEnrolmentId}`)}
                >
                  <Trans>View enrolment</Trans>
                </MenuItem>
                <MenuItem
                  icon={<User size={iconSize.md} />}
                  onClick={() => navigate(`/student/${student.id}`)}
                >
                  <Trans>View student profile</Trans>
                </MenuItem>
              </MenuList>
            </Menu>
            <IconButton
              aria-label={t`close drawer`}
              icon={<Icon as={X} />}
              variant="ghost"
              size="sm"
              onClick={onClose}
              data-testid={testIds.closeButton}
            />
          </HStack>
        </Flex>

        <HStack align="flex-start" gap={4} mt={4}>
          <UserAvatar
            user={{
              first_name: student.firstName,
              last_name: student.lastName,
              picture_url: student.pictureUrl,
            }}
            size="lg"
          />
          <Stack flex={1} minW={0} gap={1}>
            <HStack justify="space-between" align="center" gap={2}>
              <HStack gap={1} align="center" minW={0}>
                <Text fontSize="lg" fontWeight="bold" letterSpacing="-0.01em" lineHeight="1.3">
                  {fullName}
                </Text>
                <LoginAsButton kind="student" studentId={student.id} />
              </HStack>
              <HStack gap={0} align="center" flexShrink={0}>
                <Button
                  as={RouterLink}
                  to={`/student/${student.id}/details`}
                  variant="ghost"
                  size="sm"
                >
                  <Trans>Profile</Trans>
                </Button>
                <Box w="1px" h={pxToRem(18)} bg="border.default" mx={2} aria-hidden="true" />
                <Button
                  as={RouterLink}
                  to={`/enrolment/${activeEnrolmentId}`}
                  variant="ghost"
                  size="sm"
                >
                  <Trans>Enrolment</Trans>
                </Button>
              </HStack>
            </HStack>
            {course != null ? (
              <>
                <Text fontSize="lg" fontWeight="bold" letterSpacing="-0.01em" lineHeight="1.3">
                  {course.title}
                </Text>
                <HStack gap={2} flexWrap="wrap" fontSize="sm" color="fg.muted" mt={1}>
                  {course.identifier != null ? <Text>{course.identifier}</Text> : null}
                  {course.accredited === true ? (
                    <>
                      <Text color="border.emphasized" aria-hidden="true">
                        ·
                      </Text>
                      <HStack gap={1}>
                        <Icon
                          as={BadgeCheck}
                          color="teal.500"
                          fill="teal.500"
                          stroke="fg.inverse"
                        />
                        <Text fontWeight="semibold" color="teal.500">
                          <Trans>Accredited</Trans>
                        </Text>
                      </HStack>
                    </>
                  ) : null}
                  <Text color="border.emphasized" aria-hidden="true">
                    ·
                  </Text>
                  <HStack gap={1}>
                    <Text fontWeight="semibold" whiteSpace="nowrap">
                      <Trans>Start date:</Trans>
                    </Text>
                    <Text whiteSpace="nowrap">{djs(enrolment.startAtDate).format('D MMM YY')}</Text>
                  </HStack>
                  {student.email != null || student.secondaryEmail != null ? (
                    <>
                      <Text color="border.emphasized" aria-hidden="true">
                        ·
                      </Text>
                      <HStack gap={1}>
                        {student.email != null ? (
                          <CopyToClipboard value={student.email} label={t`Copy email address`}>
                            <Text color="fg.muted">{student.email}</Text>
                          </CopyToClipboard>
                        ) : null}
                        {student.secondaryEmail != null ? (
                          <CopyToClipboardPopover value={student.secondaryEmail} />
                        ) : null}
                      </HStack>
                    </>
                  ) : null}
                </HStack>
              </>
            ) : null}
          </Stack>
        </HStack>

        {enrolmentTabs.length > 1 ? (
          <Box mt={4} mx={-5} px={5} overflowX="auto">
            <Tabs
              variant="line"
              colorScheme="brand"
              index={Math.max(
                0,
                enrolmentTabs.findIndex((tab) => tab.id === activeEnrolmentId),
              )}
              onChange={(index) => {
                const tab = enrolmentTabs[index];
                if (tab != null) {
                  setSelectedEnrolmentId(tab.id);
                  setPlacing(null);
                }
              }}
            >
              <TabList>
                {enrolmentTabs.map((tab) => {
                  const year = tab.startAtDate != null ? djs(tab.startAtDate).format('YYYY') : null;
                  return (
                    <Tab key={tab.id} fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
                      {[tab.courseIdentifier ?? '—', year].filter(Boolean).join(' · ')}
                    </Tab>
                  );
                })}
              </TabList>
            </Tabs>
          </Box>
        ) : null}

        {journey != null ? (
          <Box mt={4} mx={-5} px={5} overflowX="auto">
            <Box minW={pxToRem(440)}>
              <PlacementStepper
                onSelectStage={
                  changeStageAvailable
                    ? (stage) => {
                        setPreselectedStage(stage);
                        changeStageModal.onOpen();
                      }
                    : undefined
                }
                journeyStatus={journey.status}
                employerPlacementStatus={activePlacement?.status ?? null}
                onHold={journey.onHold}
              />
            </Box>
          </Box>
        ) : null}
      </DrawerHeader>

      <DrawerBody bg="bg.subtle" p={5}>
        <Stack gap={4}>
          {journey == null ? (
            <Stack
              bg="bg.surface"
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="2xl"
              p={5}
              gap={1}
            >
              <Text fontWeight="semibold">
                <Trans>No work placement yet</Trans>
              </Text>
              <Text fontSize="sm" color="fg.muted">
                <Trans>Place the student in a workplace once they are ready.</Trans>
              </Text>
            </Stack>
          ) : (
            <>
              <ReviewAlert
                pendingDocuments={pendingDocuments}
                pendingNominations={pendingNominations}
              />
              <RequiredChecksSection
                journeyId={journeyId}
                courseEnrolmentId={activeEnrolmentId}
                enrolmentWithdrawn={enrolmentWithdrawn}
                prePlacementChecklistDueAt={journey.prePlacementChecklistDueAt}
                onDueDateUpdated={refetchAll}
                canEdit={canReview}
                canUploadDocuments={canUploadDocuments}
                journeyPlaced={journeyPlaced}
              />
              <ReverseMarketingSection
                journeyId={journeyId}
                enrolmentWithdrawn={enrolmentWithdrawn}
                courseEnrolmentId={activeEnrolmentId}
                canEdit={canReview}
                nominationsSubmitted={journey.nominationsSubmittedAt != null}
                maxNominations={journey.maxNominations ?? null}
                journeyPlaced={journeyPlaced}
              />
              {journeyId != null ? (
                <ContractSection
                  journeyId={journeyId}
                  journeyStatus={journey.status}
                  journeyOnHold={journeyOnHold}
                  enrolmentWithdrawn={enrolmentWithdrawn}
                  isRefreshing={journeyIsPlaceholder || studentIsPlaceholder}
                  courseId={course?.id ?? null}
                  contract={contract}
                  onChanged={refetchAll}
                />
              ) : null}
              {activePlacement != null ? (
                <PlacementDetailsSection
                  placement={activePlacement}
                  headerActions={placementActions}
                />
              ) : (
                <Stack
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border.default"
                  borderRadius="2xl"
                  p={5}
                  gap={4}
                  data-testid={testIds.placementDetailsSection}
                >
                  <HStack justify="space-between" align="center" gap={3} flexWrap="wrap">
                    <Stack gap={1}>
                      <Text fontSize="lg" fontWeight="semibold">
                        <Trans>Placement</Trans>
                      </Text>
                      <Text fontSize="sm" color="fg.muted">
                        {journey.status === StudentWorkPlacementJourneyStatus.COMPLETED ? (
                          <Trans>No active placement.</Trans>
                        ) : (
                          <Trans>No active placement yet.</Trans>
                        )}
                      </Text>
                    </Stack>
                    {placementActions}
                  </HStack>
                </Stack>
              )}
              <PlacementHistorySection placements={historyPlacements} />
            </>
          )}
        </Stack>
      </DrawerBody>

      {journey != null && changeStageAvailable && journeyId != null ? (
        <ChangeStageModal
          isOpen={changeStageModal.isOpen}
          onClose={changeStageModal.onClose}
          journeyId={journeyId}
          currentStatus={journey.status}
          initialStage={preselectedStage}
          onJourneyChanged={refetchAll}
        />
      ) : null}

      {placing != null ? (
        <CreatePlacementModal
          isOpen
          onClose={() => setPlacing(null)}
          {...placing}
          onSuccess={() => {
            refetchAll();
            setPlacing(null);
          }}
        />
      ) : null}
    </>
  );
}
